import {
  httpGet,
  httpPost,
  httpPatch,
  httpPatchMerge,
  httpDelete,
} from './http'
import type { TEntityId } from '~/types/models'
import type {
  ISupplierProduct,
  ISupplierProductOverview,
  ISupplierProductWithItemsPropertyPopulated,
} from '~/types/supplierProduct/models'
import {
  collectOptionalItemsWithContext,
  getSupplierProductLabel,
  stripRootLevelProductGroup,
} from '~/cynqueModules/dataObjects/SupplierProduct'
import { supplierProductHasUnconditionalContent } from '~/cynqueModules/kitOptionContent'

const apiEntityPath = '/api/supplierproducts'

export async function getSupplierProductsList(id: string) {
  const callingMn = getSupplierProductsList.name
  const apiPath = `/api/suppliers/${id}/supplierProducts`
  const raw = await httpGet<ISupplierProduct[]>({ callingMn, apiPath })
  // TEMP TESTING SHIM — strip root-level productGroup (see
  // stripRootLevelProductGroup).
  return raw.map((sp) => stripRootLevelProductGroup(sp))
}

export async function getSupplierProductOverview(id: string) {
  const callingMn = getSupplierProductOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overview = await httpGet<ISupplierProductOverview>({
    callingMn,
    apiPath,
  })
  // TEMP TESTING SHIM — strip root-level productGroup off the embedded
  // supplier-product.
  overview.supplierProduct = stripRootLevelProductGroup(
    overview.supplierProduct
  )
  return overview
}

export async function getSupplierProductWithItems(
  id: string
): Promise<ISupplierProductWithItemsPropertyPopulated> {
  const callingMn = getSupplierProductWithItems.name
  const apiPath = `${apiEntityPath}/${id}/withitems`
  const supplierProduct =
    await httpGet<ISupplierProductWithItemsPropertyPopulated>({
      callingMn,
      apiPath,
    })
  // TEMP TESTING SHIM — strip root-level productGroup (recurses children).
  return stripRootLevelProductGroup(supplierProduct)
}

export async function getSupplierProductItemsForSupplierProduct(id: string) {
  const supplierProduct = await getSupplierProductWithItems(id)
  return supplierProduct.items
}

export async function getOptionalSupplierProductItemsForSupplierProduct(
  id: string
) {
  const fn = getOptionalSupplierProductItemsForSupplierProduct.name
  const supplierProduct = await getSupplierProductWithItems(id)

  const justItems = collectOptionalItemsWithContext({ supplierProduct })
  console.log(`${fn} collected optional items with context:`, justItems)

  // Whether the kit builds anything with nothing picked, for the "Nothing
  // would be added" check (cynqueModules/kitOptionContent.ts). On every
  // option, since the options list is all the validator is handed.
  const kitHasUnconditionalContent =
    supplierProductHasUnconditionalContent(supplierProduct)
  justItems.forEach((item) => {
    ;(item as any)._kitHasUnconditionalContent = kitHasUnconditionalContent
    const spSegment =
      item.parentSupplierProductItem?.descr ||
      getSupplierProductLabel(item.supplierProduct) ||
      getSupplierProductLabel(supplierProduct)
    item.label = `${spSegment} | ${item.descr || item.product.descr} `
  })
  console.log(`${fn} just optional items with labels:`, justItems)

  // let justItemsPlusDividers: any[] = []
  // justItems.forEach((item, index) => {
  //   justItemsPlusDividers.push(item)
  //   if (index < justItems.length - 1) {
  //     if (
  //       justItems[index + 1]?.label.split('|')[0] !== item?.label.split('|')[0]
  //     ) {
  //       for (let i = 0; i < 2; i++) {
  //         justItemsPlusDividers.push({ divider: true })
  //       }
  //     }
  //   }
  // })
  const justItemsPlusDividers: any[] = []
  justItems.forEach((item, index) => {
    justItemsPlusDividers.push(item)
    if (index < justItems.length - 1) {
      if (
        justItems[index + 1]?._selectUid.split('__')[1] !==
        item?._selectUid.split('__')[1]
      ) {
        for (let i = 0; i < 2; i++) {
          // V3 shape: Vuetify 2 read a bare `divider: true` on a select item;
          // Vuetify 3 keys off `item.type` (VList.js:25 itemTypes) and renders a
          // real VDivider for it (VSelect.js:514). Under V2's shape the entry had
          // no title and stayed selectable, so each one showed as a blank,
          // caption-less checkbox in the menu. Per-item props go under `props`.
          justItemsPlusDividers.push({ type: 'divider' })
        }
      }
    }
  })
  console.log(
    `${fn} just optional items with labels and dividers:`,
    justItemsPlusDividers
  )
  return justItemsPlusDividers
}

export async function createSupplierProduct(entity: ISupplierProduct) {
  const callingMn = createSupplierProduct.name
  const apiPath = apiEntityPath
  return httpPost<ISupplierProduct>({ callingMn, apiPath, entity })
}

export async function updateSupplierProduct(
  entityData: Partial<ISupplierProduct>,
  id: TEntityId
) {
  const callingMn = updateSupplierProduct.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialISupplierProduct = Partial<ISupplierProduct>
  return httpPatchMerge<TPartialISupplierProduct>({
    callingMn,
    apiPath,
    entityData,
  })
}

export function getRouteForOnCreated(id: string) {
  return { name: 'SupplierProducts-id', params: { id } }
}

export async function updateSupplierProductSeqNums(
  updateData: Partial<ISupplierProduct>[]
) {
  const callingMn = updateSupplierProductSeqNums.name
  const apiPath = apiEntityPath
  return httpPatch({
    callingMn,
    apiPath,
    entityData: updateData,
  })
}

export async function deleteSupplierProduct(entityData: ISupplierProduct) {
  const callingMn = deleteSupplierProduct.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}
